import { getStoredBudgetMismatch } from '@statura/utils/award-data/sah';
import type { ClientRecordAction } from '@/lib/clients/client-record-action-queue';
import { formatBudgetVintageMismatchDetail } from '@/lib/clients/zone-d-first-paint';
import { formatEnum } from '@/lib/format';
import {
  sahBudgetFundingHref,
  sahClientClassificationEditorHref,
  sahClientConsentsHref,
  sahClientCreateAgreementHref,
  sahClientClinicalTabHref,
  sahClientMacIdHref,
} from '@/lib/sah/sah-href-contract';

/**
 * Zone D — open typed notice registry for the client-record overview.
 *
 * Each notice type has an explicit producer so "no notices" provably means
 * all-clear (spec §3 Zone D). Iterate this list in tests; do not re-derive
 * the union by hand or a new member can vanish from paint coverage.
 */
export const CLIENT_RECORD_NOTICE_TYPES = [
  'budget_stored_canonical_mismatch',
  'classification_source_mismatch',
  'missing_acd',
  'missing_mac_id',
  'blocking_service_agreement',
  'open_hospital_episode',
  'expiring_evidence_or_consent',
  'clinical_risk_flag',
  'budget_near_exhaustion',
  'active_alert',
] as const;

export type ClientRecordNoticeType = (typeof CLIENT_RECORD_NOTICE_TYPES)[number];

/** Anchor id on the Zone A header StatPill (and Overview strip when visible) for MAC deep links. */
export const CLIENT_RECORD_MAC_ID_ANCHOR = 'mac-id';

export type ClientRecordNoticeSeverity = 'warning' | 'error' | 'info';

export type ClientRecordNotice = {
  id: string;
  type: ClientRecordNoticeType;
  severity: ClientRecordNoticeSeverity;
  /** Care attention must never mask an unavailable upstream source. */
  classification: 'care-attention' | 'read-failure';
  title: string;
  detail: string;
  href?: string;
  cta?: string;
};

/** Only a nonempty, wholly classified care list can be treated as care attention. */
export function classifyClientRecordNotices(
  notices: readonly ClientRecordNotice[],
): 'care-attention' | 'read-failure' | 'unclassified' {
  if (notices.some((notice) => notice.classification === 'read-failure')) return 'read-failure';
  return notices.length > 0 && notices.every((notice) => notice.classification === 'care-attention')
    ? 'care-attention'
    : 'unclassified';
}

export type BudgetStoredCanonicalMismatchInput = {
  clientId: string;
  storedAnnual?: number | null;
  storedQuarterly?: number | null;
  classificationLevel?: number | null;
  transitionedHcpLevel?: number | null;
  /** Quarter start (YYYY-MM-DD) of the stored budget; its schedule is the one compared. */
  asOfDate?: string | null;
};

export function produceBudgetStoredCanonicalMismatchNotice(
  input: BudgetStoredCanonicalMismatchInput,
): ClientRecordNotice | null {
  const mismatch = getStoredBudgetMismatch({
    storedAnnual: input.storedAnnual ?? null,
    storedQuarterly: input.storedQuarterly ?? null,
    classificationLevel: input.classificationLevel ?? null,
    transitionedHcpLevel: input.transitionedHcpLevel ?? null,
    asOfDate: input.asOfDate ?? null,
  });
  if (!mismatch) return null;

  return {
    id: `budget-mismatch-${input.clientId}`,
    type: 'budget_stored_canonical_mismatch',
    classification: 'care-attention',
    severity: 'warning',
    title: 'Budget amount needs review',
    detail: formatBudgetVintageMismatchDetail({
      field: mismatch.field,
      stored: mismatch.stored,
      canonical: mismatch.canonical,
      resolutionLabel: mismatch.resolutionLabel,
      canonicalScheduleLabel: mismatch.canonicalScheduleLabel,
      storedScheduleLabel: mismatch.storedScheduleLabel,
    }),
    href: sahBudgetFundingHref(input.clientId),
    cta: 'Review in Budget',
  };
}

export type ClassificationSourceMismatchInput = {
  clientId: string;
  /** clients.classification_level — denormalized cache the header pill / budget model read. */
  denormalizedLevel?: number | null;
  /** Current-effective sah_classifications.classification_level — the authoritative SSOT. */
  effectiveLevel?: number | null;
};

/**
 * Fires when the denormalized clients.classification_level diverges from the current
 * effective sah_classifications level. The effective-dated assessment outcome is
 * authoritative; the record column is a cache that can go stale (seed/import drift).
 * Surface it — do NOT silently repoint readers (the divergence must be reconciled at
 * source, and other stored figures may be inconsistent too).
 */
export function produceClassificationSourceMismatchNotice(
  input: ClassificationSourceMismatchInput,
): ClientRecordNotice | null {
  const denorm = input.denormalizedLevel ?? null;
  const effective = input.effectiveLevel ?? null;
  if (denorm == null || effective == null) return null;
  if (denorm === effective) return null;
  return {
    id: `classification-source-mismatch-${input.clientId}`,
    type: 'classification_source_mismatch',
    classification: 'care-attention',
    severity: 'warning',
    title: 'SAH level differs from current assessment',
    detail: `The record shows SAH Level ${denorm}, but the current assessment outcome on file is Level ${effective}. The assessment outcome is authoritative — reconcile the record before relying on the level, budget, or contribution figures.`,
    href: sahClientClassificationEditorHref(input.clientId),
    cta: 'Reconcile classification',
  };
}

export type MissingMacIdNoticeInput = {
  clientId: string;
  /** SAH-funded client with no MAC ID on file (intake/reconciliation gap). */
  showMissingMacNotice: boolean;
};

export function produceMissingMacIdNotice(
  input: MissingMacIdNoticeInput,
): ClientRecordNotice | null {
  if (!input.showMissingMacNotice) return null;
  return {
    id: `missing-mac-${input.clientId}`,
    type: 'missing_mac_id',
    classification: 'care-attention',
    severity: 'warning',
    title: 'My Aged Care ID not captured',
    detail:
      'Record the My Aged Care ID so classification, budgets, service authorisations and government reporting reconcile from one participant record.',
    href: sahClientMacIdHref(input.clientId),
    cta: 'Add My Aged Care ID',
  };
}

export type BlockingServiceAgreementNoticeInput = {
  clientId: string;
  /** Delivery-readiness G-C: signed service agreement unsatisfied. */
  agreementUnsigned: boolean;
};

export function produceBlockingServiceAgreementNotice(
  input: BlockingServiceAgreementNoticeInput,
): ClientRecordNotice | null {
  if (!input.agreementUnsigned) return null;
  return {
    id: `blocking-agreement-${input.clientId}`,
    type: 'blocking_service_agreement',
    classification: 'care-attention',
    severity: 'error',
    title: 'Signed service agreement required',
    detail:
      'A signed Support at Home service agreement is required before funded delivery, participant statements, and Services Australia claims can proceed.',
    href: sahClientCreateAgreementHref(input.clientId),
    cta: 'Complete agreement',
  };
}

export type MissingAcdNoticeInput = {
  clientId: string;
  showNoAcdBanner: boolean;
  showIntakeAcpBanner: boolean;
  person?: string;
};

export function produceMissingAcdNotice(input: MissingAcdNoticeInput): ClientRecordNotice | null {
  const thisPerson = (input.person ?? 'Client').toLowerCase();
  if (!input.showNoAcdBanner && !input.showIntakeAcpBanner) return null;
  if (input.showNoAcdBanner) {
    return {
      id: `missing-acd-${input.clientId}`,
      type: 'missing_acd',
      classification: 'care-attention',
      severity: 'error',
      title: 'Advance care directive not on file',
      detail: `No advance care directive (ACD) is recorded for this ${thisPerson}. Capture or link directive evidence before clinical handoffs.`,
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=documents&panel=required-evidence#required-evidence`,
      cta: 'Review documents',
    };
  }
  return {
    id: `intake-acp-${input.clientId}`,
    type: 'missing_acd',
    classification: 'care-attention',
    severity: 'warning',
    title: 'ACP intake marker recorded',
    detail:
      'An advance care planning intake marker is on file without a linked directive document.',
    href: `/advance-care-plans?client=${encodeURIComponent(input.clientId)}`,
    cta: 'Review directive',
  };
}

export type OpenHospitalEpisodeNoticeInput = {
  clientId: string;
  person?: string;
  episode: {
    id: string;
    hospital_name: string | null;
    expected_return_date: string | null;
    expectedReturnLabel?: string | null;
  } | null;
};

export function produceOpenHospitalEpisodeNotice(
  input: OpenHospitalEpisodeNoticeInput,
): ClientRecordNotice | null {
  if (!input.episode) return null;
  const hospital = input.episode.hospital_name ? ` at ${input.episode.hospital_name}` : '';
  const returnLabel =
    input.episode.expectedReturnLabel ??
    (input.episode.expected_return_date ? input.episode.expected_return_date : null);
  const returnHint = returnLabel ? ` Expected return ${returnLabel}.` : '';
  const person = input.person ?? 'Client';
  return {
    id: `hospital-episode-${input.episode.id}`,
    type: 'open_hospital_episode',
    classification: 'care-attention',
    severity: 'warning',
    title: `${person} in hospital`,
    detail: `An open hospital admission is recorded${hospital}.${returnHint}`,
    href: `/clients/${encodeURIComponent(input.clientId)}?tab=clinical`,
    cta: 'View on Clinical',
  };
}

export type ExpiringEvidenceOrConsentNoticeInput = {
  clientId: string;
  acdStatus?: string | null;
  acdValidTo?: string | null;
  acdValidToLabel?: string | null;
  communicationNeeds?: boolean | null;
  interpreterRequired?: boolean;
  interpreterLanguageName?: string | null;
  showPreferredLanguageNotice?: boolean;
  preferredLanguageName?: string | null;
  /**
   * Core consent presence on the client record. Supplied as explicit booleans
   * (not undefined) when the caller has resolved the consent_forms JSONB —
   * absence of consent is a typed Zone D gap, mirroring the compliance strip.
   */
  generalConsentOnFile?: boolean;
  sharingConsentOnFile?: boolean;
  /** Optional taxonomy labels when a program no longer uses the legacy names. */
  generalConsentLabel?: string;
  sharingConsentLabel?: string;
  /** Deep link for missing core consent capture. */
  consentCaptureHref?: string;
  /** Profile review deep link — Journey when Profile tab is retired under unified queue. */
  profileReviewHref?: string;
  person?: string;
};

/**
 * Canonical `consents.consent_type` tokens for the two core onboarding
 * purposes, per cohort.
 *
 * The SAH tiered intake and the non-SAH intake record the SAME two purposes
 * under DIFFERENT canonical tokens — see `createOnboardingConsentRows` /
 * `recordRedesignConsentRows` in `app/(dashboard)/clients/actions.ts`:
 *
 *   SAH tiered path -> SERVICE_AGREEMENT + COLLECT_INFO
 *   non-SAH path    -> CARE              + DATA_SHARING
 *
 * That vocabulary split is why the notice labels differ per cohort too. Reading
 * the non-SAH cohort against the SAH tokens would raise a FALSE "core consents
 * not on file" alarm for every non-SAH client who has consented, so the token
 * pair MUST be selected per cohort rather than shared.
 */
export const CORE_CONSENT_TYPES = {
  sah: { general: 'SERVICE_AGREEMENT', sharing: 'COLLECT_INFO' },
  nonSah: { general: 'CARE', sharing: 'DATA_SHARING' },
} as const;

export type CoreConsentOnFileInput = {
  isSahFunded: boolean;
  /** Whether the canonical `consents` ledger query has settled. */
  canonicalConsentsFetched: boolean;
  /** GIVEN `consent_type` tokens from the canonical `consents` ledger. */
  grantedCanonicalConsentTypes: ReadonlySet<string>;
  /**
   * Legacy `clients.consent_forms` intake snapshot. Still dual-written at
   * intake, so it is retained as a trailing fallback on the non-SAH cohort ONLY
   * — a canonical write that failed soft at intake must not flip a client the
   * UI already reads as consented back to "not on file". It is deliberately NOT
   * consulted on the SAH cohort, whose read has been canonical-only since the
   * tiered consent redesign.
   */
  legacyConsentForms?: { general?: unknown; informationSharing?: unknown } | null;
};

/**
 * Resolve the two core-consent presence booleans that feed
 * `produceExpiringEvidenceOrConsentNotices`.
 *
 * The canonical per-purpose `consents` ledger is authoritative for BOTH
 * cohorts. `clients.consent_forms` is only an intake snapshot and goes stale
 * whenever consent is captured or revoked after intake (waitlist admission, the
 * consent register, or the care-worker app), which is what left clinicians a
 * false "Core consents not on file" warning on the non-SAH client workstation.
 *
 * While the ledger query is still in flight, treat consent as on file: a
 * not-yet-evaluated absence must not flash a compliance alarm.
 */
export function resolveCoreConsentOnFile(input: CoreConsentOnFileInput): {
  generalConsentOnFile: boolean;
  sharingConsentOnFile: boolean;
} {
  const tokens = input.isSahFunded ? CORE_CONSENT_TYPES.sah : CORE_CONSENT_TYPES.nonSah;
  if (!input.canonicalConsentsFetched) {
    return { generalConsentOnFile: true, sharingConsentOnFile: true };
  }
  const legacyGeneral = !input.isSahFunded && input.legacyConsentForms?.general === true;
  const legacySharing = !input.isSahFunded && input.legacyConsentForms?.informationSharing === true;
  return {
    generalConsentOnFile: input.grantedCanonicalConsentTypes.has(tokens.general) || legacyGeneral,
    sharingConsentOnFile: input.grantedCanonicalConsentTypes.has(tokens.sharing) || legacySharing,
  };
}

export function produceExpiringEvidenceOrConsentNotices(
  input: ExpiringEvidenceOrConsentNoticeInput,
): ClientRecordNotice[] {
  const thisPerson = (input.person ?? 'Client').toLowerCase();
  const notices: ClientRecordNotice[] = [];
  const profileReviewHref =
    input.profileReviewHref ?? `/clients/${encodeURIComponent(input.clientId)}?tab=overview`;
  const acdStatus = String(input.acdStatus ?? '').toUpperCase();

  if (acdStatus === 'EXPIRED' && input.acdValidToLabel) {
    notices.push({
      id: `acd-expired-${input.clientId}`,
      type: 'expiring_evidence_or_consent',
      classification: 'care-attention',
      severity: 'error',
      title: 'Advance care directive expired',
      detail: `Directive expired on ${input.acdValidToLabel}. Review and renew before clinical decisions.`,
      href: `/advance-care-plans?client=${encodeURIComponent(input.clientId)}`,
      cta: 'Review directive',
    });
  } else if (acdStatus === 'EXPIRING_SOON' && input.acdValidToLabel) {
    notices.push({
      id: `acd-expiring-${input.clientId}`,
      type: 'expiring_evidence_or_consent',
      classification: 'care-attention',
      severity: 'warning',
      title: 'Advance care directive expiring soon',
      detail: `Directive expires on ${input.acdValidToLabel}.`,
      href: `/advance-care-plans?client=${encodeURIComponent(input.clientId)}`,
      cta: 'Review directive',
    });
  }

  const missingConsents: string[] = [];
  if (input.generalConsentOnFile === false) {
    missingConsents.push(input.generalConsentLabel ?? 'general consent');
  }
  if (input.sharingConsentOnFile === false) {
    missingConsents.push(input.sharingConsentLabel ?? 'information-sharing consent');
  }
  if (missingConsents.length > 0) {
    const list =
      missingConsents.length === 2
        ? `${missingConsents[0]} and ${missingConsents[1]}`
        : missingConsents[0];
    notices.push({
      id: `consent-missing-${input.clientId}`,
      type: 'expiring_evidence_or_consent',
      classification: 'care-attention',
      severity: 'warning',
      title: missingConsents.length === 2 ? 'Core consents not on file' : 'Consent not on file',
      detail: `No ${list} is recorded for this ${thisPerson}. Capture consent before sharing or acting on the record.`,
      href: input.consentCaptureHref ?? sahClientConsentsHref(input.clientId),
      cta: 'Review consents',
    });
  }

  if (input.communicationNeeds || input.interpreterRequired) {
    notices.push({
      id: `communication-needs-${input.clientId}`,
      type: 'expiring_evidence_or_consent',
      classification: 'care-attention',
      severity: 'info',
      title: 'Communication support required',
      detail: input.interpreterRequired
        ? `Interpreter required${input.interpreterLanguageName ? ` (${input.interpreterLanguageName})` : ''}.`
        : `Documented communication needs affect how the care team engages this ${thisPerson}.`,
      href: profileReviewHref,
      cta: 'Review profile',
    });
  } else if (input.showPreferredLanguageNotice && input.preferredLanguageName) {
    notices.push({
      id: `preferred-language-${input.clientId}`,
      type: 'expiring_evidence_or_consent',
      classification: 'care-attention',
      severity: 'info',
      title: 'Preferred language recorded',
      detail: `Preferred language: ${input.preferredLanguageName}.`,
      href: profileReviewHref,
      cta: 'Review profile',
    });
  }

  return notices;
}

export type ClinicalRiskFlagNoticeInput = {
  clientId: string;
  riskLevel?: string | null;
  person?: string;
};

export function produceClinicalRiskFlagNotice(
  input: ClinicalRiskFlagNoticeInput,
): ClientRecordNotice | null {
  const risk = String(input.riskLevel ?? '').toLowerCase();
  if (!risk || risk === 'unknown' || risk === 'not_assessed' || risk === 'low') return null;
  const person = input.person ?? 'Client';
  const severity: ClientRecordNoticeSeverity =
    risk === 'critical' || risk === 'high' ? 'error' : 'warning';
  return {
    id: `clinical-risk-${input.clientId}`,
    type: 'clinical_risk_flag',
    classification: 'care-attention',
    severity,
    title: 'Standing clinical risk flag',
    detail: `${person} is flagged ${formatEnum(risk)} risk on the record.`,
    href: `${sahClientClinicalTabHref(input.clientId)}#clinical-facts`,
    cta: 'Edit clinical facts',
  };
}

export type BudgetNearExhaustionNoticeInput = {
  clientId: string;
  usedPercent: number;
};

export function produceBudgetNearExhaustionNotice(
  input: BudgetNearExhaustionNoticeInput,
): ClientRecordNotice | null {
  if (input.usedPercent < 85) return null;
  return {
    id: `budget-exhaustion-${input.clientId}`,
    type: 'budget_near_exhaustion',
    classification: 'care-attention',
    severity: input.usedPercent >= 95 ? 'error' : 'warning',
    title: 'Budget near exhaustion',
    detail: `${input.usedPercent}% of the direct-care budget is used this quarter.`,
    href: sahBudgetFundingHref(input.clientId),
    cta: 'Review budget',
  };
}

export type ActiveAlertNoticeInput = {
  clientId: string;
  openIncidentCount: number;
  sirsOutstandingCount: number;
  nextDeadlineLabel?: string | null;
};

export function produceActiveAlertNotices(input: ActiveAlertNoticeInput): ClientRecordNotice[] {
  const notices: ClientRecordNotice[] = [];
  if (input.sirsOutstandingCount > 0) {
    notices.push({
      id: `sirs-alert-${input.clientId}`,
      type: 'active_alert',
      classification: 'care-attention',
      severity: 'error',
      title: 'Serious Incident Response Scheme (SIRS) notification outstanding',
      detail: `${input.sirsOutstandingCount} reportable incident${input.sirsOutstandingCount === 1 ? '' : 's'} still require Commission notification.`,
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=incidents`,
      cta: 'Review incidents',
    });
  } else if (input.openIncidentCount > 0) {
    notices.push({
      id: `open-incidents-${input.clientId}`,
      type: 'active_alert',
      classification: 'care-attention',
      severity: 'warning',
      title: 'Open safety incidents',
      detail: input.nextDeadlineLabel
        ? `${input.openIncidentCount} open incident${input.openIncidentCount === 1 ? '' : 's'}; next deadline ${input.nextDeadlineLabel}.`
        : `${input.openIncidentCount} open incident${input.openIncidentCount === 1 ? '' : 's'} require follow-up.`,
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=incidents`,
      cta: 'Review incidents',
    });
  }
  return notices;
}

export type OverviewDataLoadErrorInput = {
  clientId: string;
  acdUnavailable?: boolean;
  hospitalEpisodeUnavailable?: boolean;
  incidentEvidenceUnavailable?: boolean;
  sahReadinessUnavailable?: boolean;
  carePlanEvidenceUnavailable?: boolean;
};

/** Surfaces upstream query failures — distinct from "no notices" all-clear (spec §9). */
export function produceOverviewDataLoadErrorNotices(
  input: OverviewDataLoadErrorInput,
): ClientRecordNotice[] {
  const notices: ClientRecordNotice[] = [];
  if (input.acdUnavailable) {
    notices.push({
      id: `acd-load-error-${input.clientId}`,
      type: 'active_alert',
      classification: 'read-failure',
      severity: 'error',
      title: "Couldn't load advance care directive status",
      detail:
        'Retry before relying on advance care directive (ACD) compliance signals on this overview.',
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=overview`,
      cta: 'Review overview',
    });
  }
  if (input.hospitalEpisodeUnavailable) {
    notices.push({
      id: `hospital-load-error-${input.clientId}`,
      type: 'active_alert',
      classification: 'read-failure',
      severity: 'error',
      title: "Couldn't load hospital episode status",
      detail: 'Retry before relying on hospital admission signals on this overview.',
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=clinical`,
      cta: 'Open clinical',
    });
  }
  if (input.incidentEvidenceUnavailable) {
    notices.push({
      id: `incident-evidence-load-error-${input.clientId}`,
      type: 'active_alert',
      classification: 'read-failure',
      severity: 'error',
      title: "Couldn't load incident status",
      detail: 'Retry before relying on safety and reportable-incident signals for this client.',
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=incidents`,
      cta: 'Review incidents',
    });
  }
  if (input.sahReadinessUnavailable) {
    notices.push({
      id: `sah-readiness-load-error-${input.clientId}`,
      type: 'active_alert',
      classification: 'read-failure',
      severity: 'error',
      title: "Couldn't load Support at Home readiness",
      detail:
        'Retry before relying on agreement, classification, budget or delivery-readiness signals for this client.',
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=sah-hub`,
      cta: 'Open Support at Home',
    });
  }
  if (input.carePlanEvidenceUnavailable) {
    notices.push({
      id: `care-plan-evidence-load-error-${input.clientId}`,
      type: 'active_alert',
      classification: 'read-failure',
      severity: 'error',
      title: "Couldn't load care plan status",
      detail: 'Retry before relying on current-plan and review signals for this client.',
      href: `/clients/${encodeURIComponent(input.clientId)}?tab=care-plans`,
      cta: 'Review care plans',
    });
  }
  return notices;
}

export type CollectClientRecordZoneDNoticesInput = {
  clientId: string;
  person?: string;
  budgetMismatch?: Omit<BudgetStoredCanonicalMismatchInput, 'clientId'> | null;
  classificationSourceMismatch?: Omit<ClassificationSourceMismatchInput, 'clientId'> | null;
  macId?: Omit<MissingMacIdNoticeInput, 'clientId'> | null;
  serviceAgreement?: Omit<BlockingServiceAgreementNoticeInput, 'clientId'> | null;
  acd?: Omit<MissingAcdNoticeInput, 'clientId'> | null;
  hospitalEpisode?: OpenHospitalEpisodeNoticeInput['episode'];
  loadErrors?: Omit<OverviewDataLoadErrorInput, 'clientId'> | null;
  expiringEvidence?: Omit<ExpiringEvidenceOrConsentNoticeInput, 'clientId'> | null;
  clinicalRisk?: Omit<ClinicalRiskFlagNoticeInput, 'clientId'> | null;
  budgetExhaustion?: Omit<BudgetNearExhaustionNoticeInput, 'clientId'> | null;
  activeAlerts?: Omit<ActiveAlertNoticeInput, 'clientId'> | null;
};

/** Collect all wired Zone D notices for the overview. */
export function collectClientRecordZoneDNotices(
  input: CollectClientRecordZoneDNoticesInput,
): ClientRecordNotice[] {
  const notices: ClientRecordNotice[] = [];
  const person = input.person;

  if (input.loadErrors) {
    notices.push(
      ...produceOverviewDataLoadErrorNotices({ clientId: input.clientId, ...input.loadErrors }),
    );
  }

  if (input.budgetMismatch) {
    const budgetNotice = produceBudgetStoredCanonicalMismatchNotice({
      clientId: input.clientId,
      ...input.budgetMismatch,
    });
    if (budgetNotice) notices.push(budgetNotice);
  }

  if (input.classificationSourceMismatch) {
    const classificationNotice = produceClassificationSourceMismatchNotice({
      clientId: input.clientId,
      ...input.classificationSourceMismatch,
    });
    if (classificationNotice) notices.push(classificationNotice);
  }

  if (input.macId) {
    const macNotice = produceMissingMacIdNotice({ clientId: input.clientId, ...input.macId });
    if (macNotice) notices.push(macNotice);
  }

  if (input.serviceAgreement) {
    const agreementNotice = produceBlockingServiceAgreementNotice({
      clientId: input.clientId,
      ...input.serviceAgreement,
    });
    if (agreementNotice) notices.push(agreementNotice);
  }

  if (input.acd && !input.loadErrors?.acdUnavailable) {
    const acdNotice = produceMissingAcdNotice({
      clientId: input.clientId,
      person,
      ...input.acd,
    });
    if (acdNotice) notices.push(acdNotice);
  }

  if (input.hospitalEpisode !== undefined && !input.loadErrors?.hospitalEpisodeUnavailable) {
    const hospitalNotice = produceOpenHospitalEpisodeNotice({
      clientId: input.clientId,
      person,
      episode: input.hospitalEpisode,
    });
    if (hospitalNotice) notices.push(hospitalNotice);
  }

  if (input.expiringEvidence) {
    notices.push(
      ...produceExpiringEvidenceOrConsentNotices({
        clientId: input.clientId,
        person,
        ...input.expiringEvidence,
      }),
    );
  }

  if (input.clinicalRisk) {
    const riskNotice = produceClinicalRiskFlagNotice({
      clientId: input.clientId,
      person,
      ...input.clinicalRisk,
    });
    if (riskNotice) notices.push(riskNotice);
  }

  if (input.budgetExhaustion) {
    const exhaustionNotice = produceBudgetNearExhaustionNotice({
      clientId: input.clientId,
      ...input.budgetExhaustion,
    });
    if (exhaustionNotice) notices.push(exhaustionNotice);
  }

  if (input.activeAlerts) {
    notices.push(...produceActiveAlertNotices({ clientId: input.clientId, ...input.activeAlerts }));
  }

  return notices;
}

/** Action queue ids that already surface the same signal as a Zone D notice type. */
const ZONE_D_ACTION_QUEUE_SUPPRESSION: Partial<Record<ClientRecordNoticeType, readonly string[]>> =
  {
    blocking_service_agreement: ['delivery-check-SIGNED_SERVICE_AGREEMENT'],
    missing_mac_id: ['capture-my-aged-care', 'confirm-hcp-transition-funding'],
  };

/** Notice-id prefixes suppressed when a specific queue action is already present. */
const ZONE_D_NOTICE_ID_SUPPRESSION: ReadonlyArray<{
  noticeIdPrefix: string;
  actionIds: readonly string[];
}> = [
  {
    noticeIdPrefix: 'consent-missing-',
    actionIds: ['delivery-check-GENERAL_CARE_CONSENT'],
  },
];

/**
 * Drop Zone D notices when the unified action queue already carries the same
 * blocker (spec §2 one-fact-one-place; Principle 8 satisfied via Zone B).
 */
export function dedupeZoneDNoticesForActionQueue(
  notices: readonly ClientRecordNotice[],
  actions: readonly Pick<ClientRecordAction, 'id'>[],
): ClientRecordNotice[] {
  const actionIds = new Set(actions.map((action) => action.id));
  return notices.filter((notice) => {
    const idSuppression = ZONE_D_NOTICE_ID_SUPPRESSION.find((rule) =>
      notice.id.startsWith(rule.noticeIdPrefix),
    );
    if (idSuppression?.actionIds.some((actionId) => actionIds.has(actionId))) {
      return false;
    }

    const suppressWhenPresent = ZONE_D_ACTION_QUEUE_SUPPRESSION[notice.type];
    if (!suppressWhenPresent) return true;
    return !suppressWhenPresent.some((actionId) => actionIds.has(actionId));
  });
}
